'use client';
import { useCallback, useSyncExternalStore } from 'react';

const listeners = new Set<() => void>();

const notifyListeners = (): void => {
  for (const listener of [...listeners]) {
    listener();
  }
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  window.addEventListener('popstate', listener);

  return (): void => {
    listeners.delete(listener);
    window.removeEventListener('popstate', listener);
  };
};

export const removeBrowserSearchParam = (name: string): string | null => {
  const searchParams = new URLSearchParams(window.location.search);
  const removedValue: string | null = searchParams.get(name);

  if (removedValue !== null) {
    searchParams.delete(name);
    const search: string = searchParams.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${search.length > 0 ? `?${search}` : ''}`
    );
    notifyListeners();
  }

  return removedValue;
};

export default function useBrowserSearchParam(
  name: string,
  serverValue: string | null = null
): string | null {
  const getSnapshot = useCallback(
    (): string | null => new URLSearchParams(window.location.search).get(name),
    [name]
  );
  const getServerSnapshot = useCallback((): string | null => serverValue, [serverValue]);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
